/**
 * Starter phrase deck. Thai is a phrase aid until the owner marks it reviewed.
 * Allergy cards stay separate from vegetarian and milder-food requests.
 * Do not treat a generated line as a medically reviewed translation.
 */

import type { ThaiPhrase, TripDocument } from "./schema.ts";

/** Unreviewed phrase aid. Not an owner-approved medical translation. */
export const MILK_ALLERGY_AID = {
  english: "This child has a milk allergy. Please do not add milk or dairy.",
  thai: "เด็กคนนี้แพ้นมครับ/ค่ะ กรุณาอย่าใส่นมครับ/ค่ะ",
  romanization: "Dek khon nee phae nom khrap/kha. Karuna ya sai nom khrap/kha.",
} as const;

const ALLERGY_STARTER_IDS = new Set(["phrase-no-milk"]);
const DIETARY_STARTER_IDS = new Set(["phrase-vegetarian", "phrase-milder-kids"]);

const STARTER_TITLES: Record<string, string> = {
  "phrase-vegetarian": "Vegetarian",
  "phrase-no-milk": "Milk allergy",
  "phrase-milder-kids": "Milder for children",
  "phrase-hello": "Hello",
  "phrase-taxi-hotel": "Taxi",
};

export const DEFAULT_THAI_PHRASES: ThaiPhrase[] = [
  {
    id: "phrase-no-milk",
    english: MILK_ALLERGY_AID.english,
    thai: MILK_ALLERGY_AID.thai,
    romanization: MILK_ALLERGY_AID.romanization,
    translationReviewed: false,
    kind: "allergy",
    title: "Milk allergy",
    reviewedBy: null,
    reviewedAt: null,
    backTranslation: "This child is allergic to milk. Please do not add milk.",
    draftProvider: "dictionary",
  },
  {
    id: "phrase-hello",
    english: "Hello / thank you",
    thai: "สวัสดีครับ/ค่ะ ขอบคุณครับ/ค่ะ",
    romanization: "Sawatdee khrap/kha. Khop khun khrap/kha.",
    translationReviewed: false,
    kind: "phrase",
    title: "Hello",
    backTranslation: "Hello. Thank you.",
    draftProvider: "dictionary",
  },
  {
    id: "phrase-taxi-hotel",
    english: "Please take us to this hotel",
    thai: "ไปที่โรงแรมนี้ครับ/ค่ะ",
    romanization: "Pai tee rong raem nee khrap/kha.",
    translationReviewed: false,
    kind: "phrase",
    title: "Taxi",
    backTranslation: "Go to this hotel, please.",
    draftProvider: "dictionary",
  },
  {
    id: "phrase-vegetarian",
    english: "One person is vegetarian",
    thai: "มีคนกินมังสวิรัติหนึ่งคนครับ/ค่ะ",
    romanization: "Mee kon gin mangsawirati neung kon khrap/kha.",
    translationReviewed: false,
    kind: "dietary",
    title: "Vegetarian",
    backTranslation: "There is one vegetarian person.",
    draftProvider: "dictionary",
  },
  {
    id: "phrase-milder-kids",
    english: "Milder food for the children, please",
    thai: "อาหารไม่เผ็ดสำหรับเด็กครับ/ค่ะ",
    romanization: "Ahaan mai phet samrap dek khrap/kha.",
    translationReviewed: false,
    kind: "dietary",
    title: "Milder for children",
    backTranslation: "Food that is not spicy, for children.",
    draftProvider: "dictionary",
  },
];

export type AllergyShowCard = {
  id: string;
  phraseId: string;
  title: string;
};

export const ALLERGY_SHOW_CARDS: AllergyShowCard[] = [
  { id: "allergy-milk", phraseId: "phrase-no-milk", title: "Milk allergy" },
  { id: "allergy-vegetarian", phraseId: "phrase-vegetarian", title: "Vegetarian" },
  { id: "allergy-milder", phraseId: "phrase-milder-kids", title: "Milder for children" },
];

export function phraseKind(phrase: ThaiPhrase): "allergy" | "dietary" | "phrase" {
  if (phrase.kind) return phrase.kind;
  if (ALLERGY_STARTER_IDS.has(phrase.id)) return "allergy";
  return DIETARY_STARTER_IDS.has(phrase.id) ? "dietary" : "phrase";
}

export function phraseTitle(phrase: ThaiPhrase): string {
  if (phrase.title?.trim()) return phrase.title;
  return STARTER_TITLES[phrase.id] ?? phrase.english;
}

export function isUnsafeUnreviewedMilk(phrase: ThaiPhrase): boolean {
  if (phrase.id !== "phrase-no-milk" || phrase.translationReviewed) return false;
  const dual = phrase.thai.includes("แพ้นม") && phrase.thai.includes("ไม่ใส่นม");
  const weakEnglish = /^please do not add milk\.?$/i.test(phrase.english.trim());
  return dual || weakEnglish || /pae\s*nom/i.test(phrase.romanization);
}

export function normalizePhrase(phrase: ThaiPhrase): ThaiPhrase {
  const kind = phraseKind(phrase);
  const title = phraseTitle(phrase);
  const next = { ...phrase, kind, title };
  if (!isUnsafeUnreviewedMilk(next)) return next;
  return {
    ...next,
    english: MILK_ALLERGY_AID.english,
    thai: MILK_ALLERGY_AID.thai,
    romanization: MILK_ALLERGY_AID.romanization,
    kind: "allergy",
    title: next.title || "Milk allergy",
    translationReviewed: false,
  };
}

export function normalizePhrases(phrases: ThaiPhrase[]): ThaiPhrase[] {
  return phrases.map(normalizePhrase);
}

export function applyPhraseSafety(doc: TripDocument): { doc: TripDocument; changed: boolean } {
  if (!doc.thaiPhrases.length) return { doc, changed: false };
  const thaiPhrases = normalizePhrases(doc.thaiPhrases);
  const changed = JSON.stringify(thaiPhrases) !== JSON.stringify(doc.thaiPhrases);
  if (!changed) return { doc, changed: false };
  return { doc: { ...doc, thaiPhrases }, changed: true };
}

export function phrasesForTrip(existing: ThaiPhrase[] | undefined | null): ThaiPhrase[] {
  if (existing == null) return DEFAULT_THAI_PHRASES;
  return normalizePhrases(existing);
}

export function allergyPhrasesForTrip(existing: ThaiPhrase[] | undefined): ThaiPhrase[] {
  return phrasesForTrip(existing).filter((p) => phraseKind(p) === "allergy");
}

export function dietaryPhrasesForTrip(existing: ThaiPhrase[] | undefined): ThaiPhrase[] {
  const phrases = phrasesForTrip(existing);
  const allergy = phrases.filter((p) => phraseKind(p) === "allergy");
  const dietary = phrases.filter((p) => phraseKind(p) === "dietary");
  return [...allergy, ...dietary];
}

export function usefulPhrasesForTrip(existing: ThaiPhrase[] | undefined): ThaiPhrase[] {
  if (existing == null) return DEFAULT_THAI_PHRASES.filter((p) => p.kind === "phrase");
  const phrases = phrasesForTrip(existing);
  const useful = phrases.filter((p) => phraseKind(p) === "phrase");
  if (useful.length) return useful;
  if (phrases.length) return phrases.filter((p) => phraseKind(p) !== "allergy");
  return [];
}

export function missingStarterPhrases(existing: ThaiPhrase[]): ThaiPhrase[] {
  const have = new Set(existing.map((row) => row.id));
  return DEFAULT_THAI_PHRASES.filter((row) => !have.has(row.id));
}

export function markPhraseReviewed(phrase: ThaiPhrase, reviewer: string, at = new Date().toISOString()): ThaiPhrase {
  return {
    ...phrase,
    translationReviewed: true,
    reviewedBy: reviewer,
    reviewedAt: at,
  };
}
